import React, { useState, useEffect } from "react";
import { useToast } from "../../../contexts/ToastContext";
import {
  getEventAttendees,
  ngoMarkAsPaid,
  ngoMarkAsUnPaid,
  resendRegistrationEmail,
  viewAttendeePaymentReceipt,
} from "../../../services/eventService";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { decodeBase64Number } from "../../../utils/encodeDecode";

export interface User {
  id: number | null;
  name: string;
  email: string;
}

export interface HostPayoutQueues {
  status_label: string;
  scheduled_at_formatted: string;
  processed_at_formatted: string | null;
  stripe_transfer_id: string | null;
  refund_status_label: string | null;
  refund_processed_at_formatted: string | null;
  refund_stripe_transfer_id: string | null;
}

export interface EventAttendee {
  id: number;
  event_id: number;
  user_id: number;
  User: User;
  HostPayoutQueues: HostPayoutQueues;
  number_of_attendees: number;
  manual_attendee_email: string | null;
  manual_attendee_name: string | null;
  registered_at: string;
  cancelled_at: string | null;
  status: "registered" | "cancelled" | "checked-in" | "refunded";
  status_label: "Registered" | "Cancelled" | "Checked In" | "Refunded";
  registered_at_formatted: string;
  cancelled_at_formatted: string | null;
  amount: number;
  discount_amount: number;
  host_amount: number;
  donation_amount: number;
  service_amount: number;
  payment_process_amount: number;
  payment_status: string;
  payment_status_label: string;
  payment_id: string;
  refund_id: string | null;
  registration_token: string | null;
}

const useEventAttendeeListLogic = () => {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.is_admin || false;
  const { id } = useParams(); // <-- this gets "2" from /events/edit/2
  const eventIdEncoded = id ? id : null;
  const eventId = eventIdEncoded ? decodeBase64Number(eventIdEncoded) : null;

  const { showSuccessToast, showErrorToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendees, setEventAttendees] = useState<EventAttendee[]>([]);
  const [eventDetails, setEventDetails] = useState<any>(null); // Replace 'any' with the actual type if available
  const [isEventDateIsToday, setIsEventDateIsToday] = useState(false);
  const [attendeeLimit, setAttendeeLimit] = useState<number | null>(null);
  const [attendeeRegistered, setAttendeeRegistered] = useState<number | null>(
    null,
  );
  const [attendeeCheckedIn, setAttendeeCheckedIn] = useState<number | null>(
    null,
  );

  const fetchEventAttendees = async (eventId: number) => {
    if (!eventId) {
      return;
    }
    try {
      setLoading(true);
      const res = await getEventAttendees(eventId);
      setEventAttendees(res.data.data.attendees);
      setEventDetails(res.data.data.eventDetails);
      setTotalDonationAmount(res.data.data.totalDonation);
      setTotalAmount(res.data.data.totalAmount);
      setTotalHostAmount(res.data.data.totalHostAmount);
      setTotalHostSettledAmount(res.data.data.totalHostSettledAmount);
      setTotalHostUnSettledAmount(res.data.data.totalHostUnSettledAmount);
      setTotalHostFailedAmount(res.data.data.totalHostFailedAmount);

      setIsEventDateIsToday(
        new Date().toDateString() ===
          new Date(res.data.data.eventDetails.datetime).toDateString(),
      );
      setAttendeeLimit(res.data.data.attendeeLimit);
      setAttendeeRegistered(res.data.data.attendeeRegistered);
      setAttendeeCheckedIn(res.data.data.totalCheckedInAttendees);
    } catch (err) {
      const errorResponse = (err as any)?.response?.data || err;
      const statusCode = (err as any)?.status || 400;
      showErrorToast(
        errorResponse?.message || "Failed to fetch event attendees.",
      );
      if (statusCode === 403) {
        navigate(-1);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkIsEventDatePassed = () => {
    if (!eventDetails) return false;
    const currentDate = new Date();
    const eventDate = new Date(eventDetails.datetime);
    return eventDate < currentDate;
  };

  const [paymentReceiptLoading, setPaymentReceiptLoading] = useState(false);

  const handleShowPaymentReceipt = async (
    userId: number,
    eventAttendeeId: number | null = null,
  ) => {
    try {
      if (paymentReceiptLoading) return;
      setPaymentReceiptLoading(true);
      if (!eventId) return;
      if (!userId) return;
      const response = await viewAttendeePaymentReceipt(
        eventId,
        userId,
        eventAttendeeId,
      );
      window.open(
        response.data.data.paymentIntent.latest_charge.receipt_url,
        "_blank",
      );
      showSuccessToast("Payment receipt opened in a new tab.");
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(errorResponse?.message || "Failed to fetch receipt.");
    } finally {
      setPaymentReceiptLoading(false);
    }
  };

  const [totalDonationAmount, setTotalDonationAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalHostAmount, setTotalHostAmount] = useState(0);
  const [totalHostSettledAmount, setTotalHostSettledAmount] = useState(0);
  const [totalHostUnSettledAmount, setTotalHostUnSettledAmount] = useState(0);
  const [totalHostFailedAmount, setTotalHostFailedAmount] = useState(0);

  const [markAsPaidLoading, setMarkAsPaidLoading] = useState(false);

  const handleMarkAsPaid = async (ngoId: number) => {
    try {
      if (!eventId) return;
      if (!ngoId) return;
      setMarkAsPaidLoading(true);
      const response = await ngoMarkAsPaid(eventId, ngoId);
      showSuccessToast(response.data.message);
      fetchEventAttendees(eventId);
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(errorResponse?.message || "Failed to mark as paid.");
    } finally {
      setMarkAsPaidLoading(false);
    }
  };

  const handleMarkAsUnPaid = async (ngoId: number) => {
    try {
      if (!eventId) return;
      if (!ngoId) return;
      setMarkAsPaidLoading(true);
      const response = await ngoMarkAsUnPaid(eventId, ngoId);
      showSuccessToast(response.data.message);
      fetchEventAttendees(eventId);
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(errorResponse?.message || "Failed to mark as paid.");
    } finally {
      setMarkAsPaidLoading(false);
    }
  };

  const [resendEmailLoading, setResendEmailLoading] = useState(false);

  const handleResendRegistrationEmail = async (
    eventAttendeeId: number | null,
  ) => {
    if (!eventId) return;
    if (!eventAttendeeId) return;
    try {
      setResendEmailLoading(true);
      const response = await resendRegistrationEmail(eventId, eventAttendeeId);
      showSuccessToast(response.data.message);
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(
        errorResponse?.message || "Failed to cancel registration.",
      );
    } finally {
      setResendEmailLoading(false);
    }
  };

  useEffect(() => {
    if (!eventId) {
      showErrorToast("Invalid Event.");
      navigate("/events");
    } else {
      fetchEventAttendees(eventId);
    }
  }, []);

  return {
    isAdmin,
    eventId,
    attendees,
    eventDetails,
    loading,
    navigate,
    paymentReceiptLoading,
    handleShowPaymentReceipt,
    totalDonationAmount,
    totalAmount,
    totalHostAmount,
    totalHostSettledAmount,
    totalHostUnSettledAmount,
    totalHostFailedAmount,
    handleMarkAsPaid,
    markAsPaidLoading,
    handleMarkAsUnPaid,
    isEventDateIsToday,
    attendeeLimit,
    attendeeRegistered,
    attendeeCheckedIn,
    resendEmailLoading,
    handleResendRegistrationEmail,
    fetchEventAttendees,
    checkIsEventDatePassed,
  };
};

export default useEventAttendeeListLogic;
